/**
 * Chronological business-function sequence for prioritization surfaces.
 *
 * The Prioritization Matrix (and its Excel export) arranges functional-area sections in the
 * order the business processes occur along the value chain - front office to back office to
 * platform - NOT by priority rank: Lead to Order -> Order to Cash -> Procure to Pay ->
 * Hire to Retire -> Acquire to Dispose -> Record to Report -> System Integrations ->
 * Data Architecture. Priority still matters visually (Critical/High sections are highlighted),
 * but position is chronological.
 *
 * Deterministic on purpose (no model calls): raw area names fold through
 * `normalizeBusinessProcessAlias` first, then squash-match against the per-stage alias
 * vocabulary below. Areas that match no stage return {@link BUSINESS_FUNCTION_UNRANKED} and
 * callers order them after the ranked stages (typically by graph priority, then name).
 */
import { normalizeBusinessProcessAlias } from './business-process.js';
/** The canonical chronological stages, in display order (value chain first, platform last). */
export const BUSINESS_FUNCTION_SEQUENCE = [
    'Lead to Order',
    'Order to Cash',
    'Procure to Pay',
    'Hire to Retire',
    'Acquire to Dispose',
    'Record to Report',
    'System Integrations',
    'Data Architecture',
];
/** Rank returned for an area that matches no chronological stage (sorts after all stages). */
export const BUSINESS_FUNCTION_UNRANKED = Number.MAX_SAFE_INTEGER;
/**
 * Per-stage alias vocabularies (index-aligned with {@link BUSINESS_FUNCTION_SEQUENCE}).
 * Entries are matched squashed (lowercase, alphanumerics only), AFTER business-process alias
 * folding - so `o2c`, `OTC`, and `SuiteBilling` all land on Order to Cash without repeating
 * every spelling here. Whole-token matches only: 'data' never matches 'Data Analytics'.
 */
const STAGE_ALIASES = [
    // Lead to Order - the front of the funnel (CRM, quoting).
    [
        'lead to order',
        'l2o',
        'lead to quote',
        'quote to order',
        'lead to cash',
        'l2c',
        'quote to cash',
        'q2c',
        'crm',
        'customer relationship management',
        'lead management',
        'sales and marketing',
    ],
    // Order to Cash - fulfillment, billing, receivables.
    [
        'order to cash',
        'billing',
        'invoice to cash',
        'i2c',
        'revenue management',
        'accounts receivable',
    ],
    // Procure to Pay - sourcing, purchasing, payables.
    [
        'procure to pay',
        'procurement',
        'source to pay',
        's2p',
        'accounts payable',
        'requisition to check',
        'req to check',
    ],
    // Hire to Retire - people processes.
    ['hire to retire', 'human resources', 'human capital management', 'payroll'],
    // Acquire to Dispose - fixed assets.
    ['acquire to dispose', 'fixed assets', 'asset management', 'acquire to retire'],
    // Record to Report - the financial close and reporting backbone.
    [
        'record to report',
        'financial management',
        'general ledger',
        'gl',
        'financial close',
        'close to disclose',
        'finance',
    ],
    // System Integrations - cross-system plumbing.
    [
        'system integrations',
        'system integration',
        'systems integration',
        'integrations',
        'integration',
        'platform and integrations',
        'middleware',
        'ipaas',
    ],
    // Data Architecture - data model, migration, master data.
    [
        'data architecture',
        'data migration',
        'data management',
        'data model',
        'master data',
        'master data management',
        'mdm',
        'data',
    ],
];
/** Squash a name for whole-token comparison: lowercase, alphanumerics only. */
function squash(value) {
    return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}
const RANK_BY_TOKEN = (() => {
    const map = new Map();
    STAGE_ALIASES.forEach((aliases, rank) => {
        const stage = BUSINESS_FUNCTION_SEQUENCE[rank];
        if (stage) {
            const token = squash(stage);
            if (!map.has(token))
                map.set(token, rank);
        }
        for (const alias of aliases) {
            const token = squash(alias);
            if (token && !map.has(token))
                map.set(token, rank);
        }
    });
    return map;
})();
/**
 * The chronological rank of a raw functional-area / business-process name: 0-based index into
 * {@link BUSINESS_FUNCTION_SEQUENCE}, or {@link BUSINESS_FUNCTION_UNRANKED} when the name matches
 * no stage (blank input included). Alias-tolerant: folds through `normalizeBusinessProcessAlias`
 * (so `R2R`, `SuiteBilling`, `Purchase to Pay` rank correctly), then squash-matches the stage
 * vocabulary above.
 */
export function businessFunctionSequenceRank(name) {
    const canonical = normalizeBusinessProcessAlias(name);
    if (!canonical)
        return BUSINESS_FUNCTION_UNRANKED;
    return RANK_BY_TOKEN.get(squash(canonical)) ?? BUSINESS_FUNCTION_UNRANKED;
}
//# sourceMappingURL=business-function-sequence.js.map